// Command search (⌘K, §1.9): fuzzy search over every command. The input is
// focused in the same tick the search opens (see openSearch) so keystrokes
// never leak into the score (T12).

import { useEffect, useMemo, useRef, useState } from 'react';
import { COMMANDS, runCommand, isEnabled, ctxNow } from '../commands/registry';
import type { Command } from '../commands/types';
import { useStore, setState } from '../state/store';
import { registerSearchFocus } from '../app/actions';
import { Icon } from './common';
import { shortcutFor } from './CommandButton';

/** Score a command against a query (higher is better; 0 = no match). */
export function scoreCommand(c: Command, q: string): number {
  const query = q.trim().toLowerCase();
  if (!query) return 1;
  const label = c.label.toLowerCase();
  const words = label.split(/[\s:/(),.…-]+/).filter(Boolean);
  const kws = (c.keywords ?? []).map((k) => k.toLowerCase());
  let total = 0;
  for (const part of query.split(/\s+/)) {
    let s = 0;
    if (label.startsWith(part)) s = 100;
    else {
      const wi = words.findIndex((w) => w.startsWith(part));
      if (wi >= 0) s = 80 - wi * 2;
      else if (label.includes(part)) s = 55;
      else if (kws.some((k) => k.startsWith(part))) s = 50;
      else if (kws.some((k) => k.includes(part))) s = 40;
      else {
        // Subsequence match.
        let i = 0;
        let gaps = 0;
        let last = -1;
        for (const ch of label) {
          if (ch === part[i]) {
            if (last >= 0 && ch !== label[last + 1]) gaps++;
            i++;
            last = label.indexOf(ch, last + 1);
            if (i === part.length) break;
          }
        }
        s = i === part.length ? Math.max(1, 20 - gaps * 2) : 0;
      }
    }
    if (s === 0) return 0;
    total += s;
  }
  // Prefer the multi-word phrase as a whole ("key sig" → "Change key signature").
  if (label.includes(query)) total += 30;
  // Shorter labels are slightly better; hidden helpers rank last.
  return total - label.length * 0.05 - (c.hidden ? 25 : 0);
}

export function searchCommands(q: string, limit = 50): Command[] {
  return COMMANDS.map((c) => ({ c, s: scoreCommand(c, q) }))
    .filter((x) => x.s > 0 && (q.trim() ? true : !x.c.hidden))
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map((x) => x.c);
}

export function CommandSearch() {
  const open = useStore((s) => s.searchOpen);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);

  useEffect(() => {
    registerSearchFocus(() => input.current?.focus());
    return () => registerSearchFocus(null);
  }, []);

  useEffect(() => {
    if (open) {
      setSel(0);
      input.current?.focus();
    } else setQ('');
  }, [open]);

  const results = useMemo(() => (open ? searchCommands(q) : []), [q, open]);
  const close = () => {
    setState({ searchOpen: false });
    setQ('');
  };
  const run = (c: Command) => {
    close();
    // Let the dialog close before a command opens another one.
    queueMicrotask(() => runCommand(c.id, undefined, { recent: true }));
  };

  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  return (
    <div className={`search-backdrop${open ? ' open' : ''}`} onMouseDown={(e) => e.target === e.currentTarget && close()} aria-hidden={!open}>
      <div className="search" role="dialog" aria-modal="true" aria-label="Search commands">
        <input
          ref={input}
          className="search-input"
          placeholder="Search commands… (e.g. “transpose”, “key sig”, “bend”)"
          value={q}
          aria-controls="search-results"
          aria-activedescendant={results[sel] ? `cmd-${results[sel].id}` : undefined}
          onChange={(e) => {
            setQ(e.target.value);
            setSel(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              close();
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSel((i) => Math.min(results.length - 1, i + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSel((i) => Math.max(0, i - 1));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const c = results[sel];
              if (c) run(c);
            }
            e.stopPropagation();
          }}
          data-testid="command-search-input"
          tabIndex={open ? 0 : -1}
        />
        <ul className="search-results" id="search-results" role="listbox" ref={list}>
          {results.map((c, i) => {
            const enabled = isEnabled(c, ctxNow());
            const key = shortcutFor(c.id);
            return (
              <li
                key={c.id}
                id={`cmd-${c.id}`}
                role="option"
                aria-selected={i === sel}
                aria-disabled={!enabled}
                className={`search-item${i === sel ? ' sel' : ''}${enabled ? '' : ' disabled'}`}
                onMouseMove={() => i !== sel && setSel(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  run(c);
                }}
              >
                <span className="search-icon">
                  <Icon icon={c.icon} size={18} />
                </span>
                <span className="search-label">{c.label}</span>
                {key && <kbd>{key}</kbd>}
              </li>
            );
          })}
          {open && results.length === 0 && <li className="search-empty">No commands match “{q}”.</li>}
        </ul>
      </div>
    </div>
  );
}
